import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SortBy, URL_PARAMS } from '../activity-feed-constants';
import { ActivityFeedFilterState } from './activity-feed-filters-constants';
import {
  FILTER_PARAM_KEYS,
  filterParamsKey,
  filterStateToParams,
  paramsToFilterState,
  paramsToSortBy,
  sanitizeFilterParams
} from './activity-feed-filters-utils';

export const useActivityFeedFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // react-router rebuilds setSearchParams on every URL change (e.g. `selected=`); reading it
  // through a ref keeps setFilterState / setSortBy stable for the filter context.
  const setSearchParamsRef = useRef(setSearchParams);
  useLayoutEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  // Keyed on the filter params only, so selecting an event (`selected=`) keeps filterState stable.
  const filterKey = useMemo(() => filterParamsKey(searchParams), [searchParams]);
  const filterState = useMemo(
    () => paramsToFilterState(new URLSearchParams(filterKey)),
    [filterKey]
  );
  const sortBy = useMemo(() => paramsToSortBy(searchParams), [searchParams]);

  const setFilterState = useCallback(
    (updater: (prev: ActivityFeedFilterState) => ActivityFeedFilterState) => {
      setSearchParamsRef.current(
        params => {
          // filterStateToParams owns only the filter-state keys (date, clients, importance, etc.);
          // carry over sort and selected-item so filter changes don't wipe them.
          const next = filterStateToParams(updater(paramsToFilterState(params)));
          for (const key of [URL_PARAMS.SORT, URL_PARAMS.SELECTED]) {
            const value = params.get(key);
            if (value !== null) {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  const setSortBy = useCallback((newSortBy: SortBy | undefined) => {
    setSearchParamsRef.current(
      params => {
        const next = new URLSearchParams(params);
        if (newSortBy !== undefined) {
          next.set(URL_PARAMS.SORT, newSortBy);
        } else {
          next.delete(URL_PARAMS.SORT);
        }
        return next;
      },
      { replace: true }
    );
  }, []);

  // Rewrite filter+sort params to their canonical form: invalid tokens are dropped, and a key is
  // removed only when nothing valid remains (or it matches the default).
  useEffect(() => {
    const sanitized = sanitizeFilterParams(searchParams);
    const needsUpdate = FILTER_PARAM_KEYS.some(key => searchParams.get(key) !== sanitized.get(key));
    if (!needsUpdate) return;
    setSearchParams(
      params => {
        const next = new URLSearchParams(params);
        for (const key of FILTER_PARAM_KEYS) {
          const val = sanitized.get(key);
          if (val !== null) {
            next.set(key, val);
          } else {
            next.delete(key);
          }
        }
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  return { filterState, setFilterState, sortBy, setSortBy };
};
